import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import SignaturePad from '../components/ui/SignaturePad';
import { generateApplicationId } from '../utils/idGenerator';
import { FiPlus, FiMoreHorizontal, FiCalendar, FiMail, FiCheckCircle, FiXCircle, FiEdit2, FiExternalLink, FiCopy, FiUser, FiUsers, FiBriefcase } from 'react-icons/fi';
import { useRef } from 'react';

const STAGES = ['Applied', 'Shortlisted', 'Interviewing', 'Selected', 'Rejected'];
const DEPARTMENTS = ['Engineering', 'Design', 'Marketing', 'Finance', 'Operations', 'HR', 'Data Science', 'Product'];
const MENTORS = ['Alice Johnson', 'Bob Smith', 'Carol White', 'David Lee', 'Eva Chen'];

export default function Recruitment() {
  const { applications, addApplication, updateApplication, settings } = usePlatform();
  const [appModal, setAppModal] = useState(false);
  const [interviewModal, setInterviewModal] = useState(null);
  const [offerModal, setOfferModal] = useState(null);
  const [signModal, setSignModal] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const sigRef = useRef(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', college: '', role: '', department: '', startDate: '', endDate: '', mentor: '', coverNote: '',
  });

  const publicURL = `${window.location.origin}${window.location.pathname}#/apply/${settings.publicSlug || 'acme'}`;

  const submitApplication = () => {
    if (!form.name || !form.email) return toast.error('Name and Email are required.');
    const app = {
      id: crypto.randomUUID(), appId: generateApplicationId(),
      ...form, stage: 'Applied',
      appliedAt: new Date().toISOString(), offerAcceptance: 'pending',
      interviewDate: null, offerLetterSigned: false, signature: null,
    };
    addApplication(app);
    toast.success(`Application ${app.appId} submitted!`);
    setForm({ name: '', email: '', phone: '', college: '', role: '', department: '', startDate: '', endDate: '', mentor: '', coverNote: '' });
    setAppModal(false);
  };

  const moveStage = (appId, newStage) => {
    updateApplication(appId, { stage: newStage, stageUpdatedAt: new Date().toISOString() });
    toast.success(`Moved to ${newStage}`);
  };

  const scheduleInterview = (app) => {
    if (!interviewModal?.date) return toast.error('Pick a date.');
    updateApplication(app.id, { stage: 'Interviewing', interviewDate: interviewModal.date, interviewTime: interviewModal.time });
    toast.success('Interview scheduled!');
    setInterviewModal(null);
  };

  const generateOffer = (app) => {
    updateApplication(app.id, { stage: 'Selected', offerGenerated: true, offerGeneratedAt: new Date().toISOString() });
    toast.success('Offer letter generated!');
    setOfferModal(app);
  };

  const saveSignature = (app) => {
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error('Please sign the offer letter.');
    const sig = sigRef.current.getDataURL();
    updateApplication(app.id, { offerLetterSigned: true, signature: sig, signedAt: new Date().toISOString(), offerAcceptance: 'accepted' });
    toast.success('Offer letter signed and accepted!');
    setSignModal(null);
  };

  const handleDragStart = (id) => setDraggingId(id);
  const handleDrop = (stage) => {
    if (draggingId) { moveStage(draggingId, stage); setDraggingId(null); setDragOver(null); }
  };

  const stageColors = {
    Applied: 'bg-slate-100 text-slate-600',
    Shortlisted: 'bg-amber-50 text-amber-700',
    Interviewing: 'bg-blue-50 text-blue-700',
    Selected: 'bg-emerald-50 text-emerald-700',
    Rejected: 'bg-rose-50 text-rose-700',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="page-title">Recruitment Pipeline</h1>
          <p className="page-subtitle">Manage intern applications from screening to onboarding.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm">
            <FiExternalLink className="text-sky-500 w-4 h-4" />
            <span className="font-mono text-xs text-slate-500 max-w-[200px] truncate">{publicURL}</span>
            <button onClick={() => { navigator.clipboard.writeText(publicURL); toast.success('URL copied!'); }} className="text-slate-400 hover:text-sky-600 transition-colors">
              <FiCopy className="w-3.5 h-3.5" />
            </button>
          </div>
          <button onClick={() => setAppModal(true)} className="flex items-center gap-2 px-5 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl font-bold text-sm shadow-lg shadow-sky-500/20 transition-all">
            <FiPlus /> New Application
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((stage) => {
          const stageApps = applications.filter(a => a.stage === stage);
          return (
            <div
              key={stage}
              className={`min-w-[280px] flex-1 rounded-[24px] p-4 transition-all ${dragOver === stage ? 'ring-2 ring-sky-400 bg-sky-50/50' : 'bg-slate-50'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(stage); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={() => handleDrop(stage)}
            >
              <div className="flex items-center justify-between mb-4 px-1">
                <div className="flex items-center gap-2">
                  <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${stageColors[stage]}`}>{stage}</span>
                  <span className="text-xs font-black text-slate-400">{stageApps.length}</span>
                </div>
              </div>
              <div className="space-y-3">
                {stageApps.map((app) => (
                  <motion.div
                    key={app.id} layout
                    draggable
                    onDragStart={() => handleDragStart(app.id)}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-sky-200 transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-bold text-slate-900 text-sm">{app.name}</h3>
                        <p className="text-xs text-slate-800 font-semibold mt-0.5">{app.role} · {app.department}</p>
                      </div>
                      <div className="w-8 h-8 rounded-xl bg-sky-50 flex items-center justify-center text-[10px] font-semibold text-brand-600">
                        {app.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono text-slate-300 mb-3">{app.appId}</div>
                    {app.interviewDate && (
                      <div className="flex items-center gap-1.5 text-[10px] text-blue-600 bg-blue-50 px-2.5 py-1.5 rounded-lg mb-2 font-bold">
                        <FiCalendar className="w-3 h-3" /> {new Date(app.interviewDate).toLocaleDateString()} {app.interviewTime}
                      </div>
                    )}
                    {app.offerLetterSigned && (
                      <div className="flex items-center gap-1.5 text-[10px] text-emerald-600 bg-emerald-50 px-2.5 py-1.5 rounded-lg mb-2 font-bold">
                        <FiCheckCircle className="w-3 h-3" /> Offer Signed
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t border-surface-50 mt-2">
                      <div className="flex gap-1">
                        {stage !== 'Rejected' && stage !== 'Selected' && (
                          <button onClick={() => setInterviewModal({ app, date: '', time: '' })} className="p-1.5 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-all" title="Schedule Interview">
                            <FiCalendar className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {stage === 'Interviewing' && (
                          <button onClick={() => generateOffer(app)} className="p-1.5 text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-lg transition-all" title="Generate Offer">
                            <FiCheckCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {stage === 'Selected' && !app.offerLetterSigned && (
                          <button onClick={() => setSignModal(app)} className="p-1.5 text-slate-300 hover:text-violet-500 hover:bg-violet-50 rounded-lg transition-all" title="Sign Offer">
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button onClick={() => moveStage(app.id, 'Rejected')} className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all" title="Reject">
                          <FiXCircle className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <select value={app.stage} onChange={e => moveStage(app.id, e.target.value)} className="text-[10px] font-bold text-slate-400 bg-transparent border-none outline-none cursor-pointer hover:text-sky-600">
                        {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </motion.div>
                ))}
                <div className="h-12 border-2 border-dashed border-slate-200 rounded-2xl flex items-center justify-center text-slate-300 text-[10px] font-bold uppercase tracking-widest">
                  Drop here
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* New Application Modal */}
      <Modal isOpen={appModal} onClose={() => setAppModal(false)} title="New Internship Application" size="lg">
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'name', label: 'Full Name', type: 'text', req: true },
            { key: 'email', label: 'Email Address', type: 'email', req: true },
            { key: 'phone', label: 'Phone Number', type: 'tel' },
            { key: 'college', label: 'College / Institution', type: 'text' },
            { key: 'role', label: 'Applied Role / Position', type: 'text' },
            { key: 'startDate', label: 'Preferred Start Date', type: 'date' },
            { key: 'endDate', label: 'Preferred End Date', type: 'date' },
          ].map(f => (
            <div key={f.key}>
              <label className="block section-title mb-2">{f.label}{f.req && ' *'}</label>
              <input type={f.type} value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 transition-all" />
            </div>
          ))}
          <div>
            <label className="block section-title mb-2">Department</label>
            <select value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 transition-all bg-white">
              <option value="">Select Department</option>
              {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="block section-title mb-2">Assign Mentor</label>
            <select value={form.mentor} onChange={e => setForm(p => ({ ...p, mentor: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400 transition-all bg-white">
              <option value="">Select Mentor</option>
              {MENTORS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block section-title mb-2">Cover Note</label>
            <textarea value={form.coverNote} onChange={e => setForm(p => ({ ...p, coverNote: e.target.value }))} rows={3} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-sky-400 transition-all resize-none" />
          </div>
          <div className="md:col-span-2 flex justify-end gap-3">
            <button onClick={() => setAppModal(false)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
            <button onClick={submitApplication} className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all">Submit Application</button>
          </div>
        </div>
      </Modal>

      {/* Interview Schedule Modal */}
      <Modal isOpen={!!interviewModal} onClose={() => setInterviewModal(null)} title="Schedule Interview">
        {interviewModal && (
          <div className="p-6 space-y-4">
            <p className="text-slate-600 font-medium">Scheduling interview for <strong>{interviewModal.app?.name}</strong></p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block section-title mb-2">Date</label>
                <input type="date" value={interviewModal.date} onChange={e => setInterviewModal(p => ({ ...p, date: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" />
              </div>
              <div>
                <label className="block section-title mb-2">Time</label>
                <input type="time" value={interviewModal.time} onChange={e => setInterviewModal(p => ({ ...p, time: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-sky-400" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setInterviewModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={() => scheduleInterview(interviewModal.app)} className="px-8 py-2.5 bg-sky-600 text-white rounded-xl font-bold text-sm hover:bg-sky-500 transition-all">Schedule</button>
            </div>
          </div>
        )}
      </Modal>

      {/* E-Signature Modal */}
      <Modal isOpen={!!signModal} onClose={() => setSignModal(null)} title="Sign Offer Letter">
        {signModal && (
          <div className="p-6 space-y-4">
            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-4 text-sm text-sky-800">
              <strong>{signModal.name}</strong> — by signing below you accept the internship offer at <strong>{settings.organization}</strong>.
            </div>
            <div>
              <label className="block section-title mb-2">Your Signature</label>
              <SignaturePad ref={sigRef} />
              <button onClick={() => sigRef.current?.clear()} className="mt-2 text-xs text-slate-800 font-semibold hover:text-slate-600 font-bold">Clear</button>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setSignModal(null)} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl font-bold text-sm">Cancel</button>
              <button onClick={() => saveSignature(signModal)} className="px-8 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm hover:bg-emerald-500 transition-all">✍️ Sign & Accept</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}